import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { DashboardLoader } from '@/components/admin/DashboardLoader';
import { getBackgroundLocation } from '@/components/admin/detail-stack';
import { StackPanel } from '@/components/admin/StackPanel';
import { RequireRole } from '@/components/auth/RequireRole';
import { ForbiddenPage } from '@/components/ForbiddenPage';
import { LoadingState } from '@/components/ui/LoadingState';
import { Spinner } from '@/components/ui/Spinner';
import { HomePage } from '@/pages/HomePage';

import type { ComponentType, JSX } from 'react';

const Signin = lazy(() => import('@/pages/public/Signin'));
const Signup = lazy(() => import('@/pages/public/Signup'));
const PublicMenu = lazy(() => import('@/pages/public/PublicMenu'));
const TermsPage = lazy(() => import('@/pages/public/legal/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/public/legal/PrivacyPage'));
const LegalNoticePage = lazy(() => import('@/pages/public/legal/LegalNoticePage'));
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'));
const AccountDeletedPage = lazy(() => import('@/pages/public/AccountDeletedPage'));
const SetPasswordPage = lazy(() => import('@/pages/public/SetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/public/ForgotPasswordPage'));
const SuperAdminShell = lazy(() => import('@/components/admin/super/SuperAdminShell'));
const TenantsListPage = lazy(() => import('@/pages/admin/super/TenantsListPage'));
const TenantDetailPage = lazy(() => import('@/pages/admin/super/TenantDetailPage'));
const SuperAdminAccountPage = lazy(() => import('@/pages/admin/super/SuperAdminAccountPage'));
const ToolsPage = lazy(() => import('@/pages/admin/super/ToolsPage'));
const DashboardPage = lazy(() => import('@/pages/admin/super/DashboardPage'));
const LogsPage = lazy(() => import('@/pages/admin/super/LogsPage'));
const RestaurantsList = lazy(() => import('@/pages/dashboard/RestaurantsList'));
const CreateRestaurant = lazy(() => import('@/pages/dashboard/CreateRestaurant'));
const BillingPage = lazy(() => import('@/pages/dashboard/BillingPage'));
const AccountPage = lazy(() => import('@/pages/dashboard/AccountPage'));
// Admin resto à onglets (TASK-037) — shell sombre + onglets, remplace
// RestaurantDetail + EditRestaurant.
const AdminShell = lazy(() => import('@/components/admin/AdminShell'));
const AdminMenus = lazy(() => import('@/pages/dashboard/admin/AdminMenus'));
const AdminProducts = lazy(() => import('@/pages/dashboard/admin/AdminProducts'));
const AdminCategories = lazy(() => import('@/pages/dashboard/admin/AdminCategories'));
const AdminAppearance = lazy(() => import('@/pages/dashboard/admin/AdminAppearance'));
const AdminSettings = lazy(() => import('@/pages/dashboard/admin/AdminSettings'));
const CreateMenu = lazy(() => import('@/pages/dashboard/CreateMenu'));
const EditMenu = lazy(() => import('@/pages/dashboard/EditMenu'));
const CreateCategory = lazy(() => import('@/pages/dashboard/CreateCategory'));
const EditCategory = lazy(() => import('@/pages/dashboard/EditCategory'));
const CreateProduct = lazy(() => import('@/pages/dashboard/CreateProduct'));
const EditProduct = lazy(() => import('@/pages/dashboard/EditProduct'));

type DetailRouteDef = [
  path: string,
  roles: Array<'tenantAdmin' | 'restaurantManager'>,
  Page: ComponentType,
];

const DETAIL_ROUTES: DetailRouteDef[] = [
  ['/dashboard/restaurants/:id/menus/new', ['tenantAdmin'], CreateMenu],
  ['/dashboard/restaurants/:id/menus/:menuId/edit', ['tenantAdmin', 'restaurantManager'], EditMenu],
  [
    '/dashboard/restaurants/:id/menus/:menuId/categories/new',
    ['tenantAdmin', 'restaurantManager'],
    CreateCategory,
  ],
  [
    '/dashboard/restaurants/:id/menus/:menuId/categories/:cid/edit',
    ['tenantAdmin', 'restaurantManager'],
    EditCategory,
  ],
  [
    '/dashboard/restaurants/:id/menus/:menuId/categories/:cid/products/new',
    ['tenantAdmin', 'restaurantManager'],
    CreateProduct,
  ],
  [
    '/dashboard/restaurants/:id/menus/:menuId/categories/:cid/products/:pid/edit',
    ['tenantAdmin', 'restaurantManager'],
    EditProduct,
  ],
];

/**
 * Fiches (création / édition de carte, section, produit). Rendues deux fois :
 * en page complète (lien direct) et, pour un lien venant d'une liste, en
 * surcouche « pile » par-dessus cette liste (`overlay`) — mêmes URLs.
 */
function detailRoutes(overlay: boolean): JSX.Element[] {
  return DETAIL_ROUTES.map(([path, roles, Page]) => {
    const page = (
      <RequireRole roles={roles}>
        <Suspense fallback={<DashboardLoader />}>
          <Page />
        </Suspense>
      </RequireRole>
    );
    return (
      <Route key={path} path={path} element={overlay ? <StackPanel>{page}</StackPanel> : page} />
    );
  });
}

function AppRoutes(): JSX.Element {
  const location = useLocation();
  const background = getBackgroundLocation(location.state);
  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />
        <Route
          path="/signin"
          element={
            <Suspense fallback={<Spinner />}>
              <Signin />
            </Suspense>
          }
        />
        <Route
          path="/signup"
          element={
            <Suspense fallback={<Spinner />}>
              <Signup />
            </Suspense>
          }
        />
        <Route
          path="/legal/cgu"
          element={
            <Suspense fallback={<Spinner />}>
              <TermsPage />
            </Suspense>
          }
        />
        <Route
          path="/legal/privacy"
          element={
            <Suspense fallback={<Spinner />}>
              <PrivacyPage />
            </Suspense>
          }
        />
        <Route
          path="/legal/mentions-legales"
          element={
            <Suspense fallback={<Spinner />}>
              <LegalNoticePage />
            </Suspense>
          }
        />
        <Route
          path="/compte-supprime"
          element={
            <Suspense fallback={<Spinner />}>
              <AccountDeletedPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/set-password"
          element={
            <Suspense fallback={<Spinner />}>
              <SetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<Spinner />}>
              <ForgotPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/r/:slug"
          element={
            <Suspense fallback={<Spinner />}>
              <PublicMenu />
            </Suspense>
          }
        />
        <Route
          path="/r/:slug/:menuSlug"
          element={
            <Suspense fallback={<Spinner />}>
              <PublicMenu />
            </Suspense>
          }
        />
        {/* Shell Super Admin (SAUX-04/06) : layout + sous-routes. */}
        <Route
          path="/admin/super"
          element={
            <RequireRole roles={['superAdmin']}>
              <Suspense fallback={<Spinner />}>
                <SuperAdminShell />
              </Suspense>
            </RequireRole>
          }
        >
          {/* Le Dashboard (SAUX-41) est l'écran d'ARRIVÉE du shell : `index`
              le rend directement, plus de redirection vers `tenants`. */}
          <Route
            index
            element={
              <Suspense fallback={<LoadingState />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<LoadingState />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="tenants"
            element={
              <Suspense fallback={<LoadingState />}>
                <TenantsListPage />
              </Suspense>
            }
          />
          <Route
            path="tenants/:tenantId"
            element={
              <Suspense fallback={<LoadingState />}>
                <TenantDetailPage />
              </Suspense>
            }
          />
          <Route
            path="logs"
            element={
              <Suspense fallback={<LoadingState />}>
                <LogsPage />
              </Suspense>
            }
          />
          <Route
            path="outils"
            element={
              <Suspense fallback={<LoadingState />}>
                <ToolsPage />
              </Suspense>
            }
          />
          {/* Mon compte Super Admin (SAUX-64) — accessible via le menu compte du
              header, pas depuis la sidebar (cf. maquette validée). */}
          <Route
            path="compte"
            element={
              <Suspense fallback={<LoadingState />}>
                <SuperAdminAccountPage />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/dashboard/restaurants"
          element={
            <RequireRole roles={['tenantAdmin']}>
              <Suspense fallback={<DashboardLoader />}>
                <RestaurantsList />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="/dashboard/billing"
          element={
            <RequireRole roles={['tenantAdmin']}>
              <Suspense fallback={<DashboardLoader />}>
                <BillingPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="/dashboard/compte"
          element={
            <RequireRole roles={['tenantAdmin']}>
              <Suspense fallback={<DashboardLoader />}>
                <AccountPage />
              </Suspense>
            </RequireRole>
          }
        />
        <Route
          path="/dashboard/restaurants/new"
          element={
            <RequireRole roles={['tenantAdmin']}>
              <Suspense fallback={<DashboardLoader />}>
                <CreateRestaurant />
              </Suspense>
            </RequireRole>
          }
        />
        {/* Admin resto à onglets (TASK-037) : layout AdminShell + onglets. */}
        <Route
          path="/dashboard/restaurants/:id"
          element={
            <RequireRole roles={['tenantAdmin', 'restaurantManager']}>
              <Suspense fallback={<DashboardLoader />}>
                <AdminShell />
              </Suspense>
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="cartes" replace />} />
          <Route
            path="produits"
            element={
              <Suspense fallback={<DashboardLoader />}>
                <AdminProducts />
              </Suspense>
            }
          />
          <Route
            path="categories"
            element={
              <Suspense fallback={<DashboardLoader />}>
                <AdminCategories />
              </Suspense>
            }
          />
          <Route
            path="cartes"
            element={
              <Suspense fallback={<DashboardLoader />}>
                <AdminMenus />
              </Suspense>
            }
          />
          <Route
            path="apparence"
            element={
              <Suspense fallback={<DashboardLoader />}>
                <AdminAppearance />
              </Suspense>
            }
          />
          <Route
            path="reglages"
            element={
              <Suspense fallback={<DashboardLoader />}>
                <AdminSettings />
              </Suspense>
            }
          />
        </Route>
        {detailRoutes(false)}
        <Route
          path="*"
          element={
            <Suspense fallback={<Spinner />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Routes>
      {background !== null ? <Routes>{detailRoutes(true)}</Routes> : null}
    </>
  );
}

export function AppRouter(): JSX.Element {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
